import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Globe, Building2, Bot, Eye, Zap, CheckCircle2 } from 'lucide-react';

/**
 * UrlToCompanyChart_3 — "dark command-deck" variant of UrlToCompanyChart.
 * Same five-step path from URL to reviewed workforce (blog07), restyled as a
 * confident dashboard: opaque slate-900 card (#0F172A), light text
 * (#F8FAFC primary, #94A3B8 secondary), teal/cyan accents (#0AB3BF, #4DD4E0)
 * and subtle gridlines. Same data, same caption, same auto-advance /
 * pause-on-hover behavior.
 */

const BG = '#0F172A';
const TEXT_PRIMARY = '#F8FAFC';
const TEXT_SECONDARY = '#94A3B8';
const ACCENT = '#0AB3BF';
const ACCENT_BRIGHT = '#4DD4E0';
const GRID = 'rgba(148, 163, 184, 0.14)';

const STEPS = [
  {
    title: 'Paste a URL',
    icon: <Globe className="w-4 h-4" />,
    detail: 'A company website or LinkedIn page is enough.',
  },
  {
    title: 'Read your public presence',
    icon: <Building2 className="w-4 h-4" />,
    detail: 'Brand voice, value props, products, personas — turned into a company profile.',
  },
  {
    title: 'Propose departments & bots',
    icon: <Bot className="w-4 h-4" />,
    detail: 'Bot roles and the processes they would run, per function.',
  },
  {
    title: 'You review',
    icon: <Eye className="w-4 h-4" />,
    detail: 'Approve, edit, or reject each proposal before anything deploys.',
  },
  {
    title: 'Bots earn autonomy',
    icon: <Zap className="w-4 h-4" />,
    detail: 'Each bot is promoted at its own pace as your approvals build confidence.',
  },
];

export const UrlToCompanyChart_3 = ({ height = 340 }: { height?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!inView || paused) return;
    const t = setInterval(() => setActive((p) => (p + 1) % STEPS.length), 2200);
    return () => clearInterval(t);
  }, [inView, paused]);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="my-8 rounded-xl overflow-hidden border p-4 sm:p-6"
      style={{
        minHeight: height,
        backgroundColor: BG,
        borderColor: GRID,
        backgroundImage: `linear-gradient(${GRID} 1px, transparent 1px), linear-gradient(90deg, ${GRID} 1px, transparent 1px)`,
        backgroundSize: '28px 28px',
      }}
    >
      <div className="space-y-2.5">
        {STEPS.map((s, i) => {
          const isActive = i === active;
          const isDone = i < active;
          return (
            <motion.div
              key={s.title}
              animate={isActive ? { scale: 1.015 } : { scale: 1 }}
              transition={{ duration: 0.25 }}
              className="relative rounded-md border px-3.5 py-3 flex items-start gap-3 transition-colors duration-300"
              style={{
                backgroundColor: isActive ? 'rgba(10, 179, 191, 0.10)' : 'rgba(15, 23, 42, 0.85)',
                borderColor: isActive ? ACCENT : GRID,
              }}
            >
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 border"
                style={{
                  borderColor: isActive || isDone ? ACCENT : GRID,
                  backgroundColor: isActive ? 'rgba(10, 179, 191, 0.18)' : 'rgba(148, 163, 184, 0.08)',
                  color: isDone || isActive ? ACCENT_BRIGHT : TEXT_SECONDARY,
                }}
              >
                {isDone ? <CheckCircle2 className="w-4 h-4" /> : s.icon}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="text-label font-semibold"
                    style={{ color: isActive ? ACCENT_BRIGHT : TEXT_PRIMARY }}
                  >
                    {i + 1}. {s.title}
                  </span>
                  {isActive && (
                    <span
                      className="text-label-2xs px-2 py-0.5 rounded-full font-medium tracking-wide"
                      style={{ backgroundColor: 'rgba(10, 179, 191, 0.18)', color: ACCENT_BRIGHT }}
                    >
                      now
                    </span>
                  )}
                </div>
                <p className="text-label-sm leading-relaxed mt-0.5" style={{ color: TEXT_SECONDARY }}>
                  {s.detail}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
      <p className="mt-3.5 text-label-sm leading-relaxed text-center" style={{ color: TEXT_SECONDARY }}>
        Figure 1. The path from URL to a reviewed, running workforce — five steps, and a human reviews before anything
        deploys. The loop pauses on hover.
      </p>
    </div>
  );
};
